const LOCAL_PART_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+$/;
const DOMAIN_PART_REGEX = /^[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
const COMMON_TLDS = new Set([
  "com",
  "net",
  "org",
  "edu",
  "gov",
  "mil",
  "int",
  "info",
  "biz",
  "name",
  "pro",
  "co",
  "io",
  "ai",
  "app",
  "dev",
  "me",
  "us",
  "uk",
  "ae",
  "ca",
  "au",
  "in",
  "de",
  "fr",
  "es",
  "it",
  "nl",
  "be",
  "ch",
  "se",
  "no",
  "dk",
  "fi",
  "pl",
  "pt",
  "ie",
  "nz",
  "sg",
  "hk",
  "jp",
  "kr",
  "cn",
  "br",
  "mx",
  "za",
  "sa",
  "qa",
  "kw",
  "bh",
  "om",
  "eg",
  "pk",
  "ph",
  "my",
  "id",
  "th",
  "vn",
  "ru",
  "ua",
  "tr",
  "gr",
  "ro",
  "cz",
  "at",
  "hu",
  "il",
  "cl",
  "ar",
  "co.uk",
  "com.au",
  "com.br",
  "co.in",
  "co.za",
  "com.mx",
  "com.sg",
  "com.hk",
]);

export function normalizeEmail(value) {
  if (value == null) {
    return "";
  }
  return String(value).trim().toLowerCase();
}

export function looksLikeEmail(value) {
  return String(value || "").includes("@");
}

export function validateEmail(value, { required = true } = {}) {
  const email = normalizeEmail(value);

  if (!email) {
    return required
      ? { valid: false, email: "", message: "Please enter your email address." }
      : { valid: true, email: "", message: "" };
  }

  if (email.length > 254) {
    return {
      valid: false,
      email,
      message: "That email address is too long. Please use a shorter one.",
    };
  }

  const atIndex = email.lastIndexOf("@");
  if (atIndex <= 0 || atIndex !== email.indexOf("@")) {
    return {
      valid: false,
      email,
      message: "That email address doesn't look valid. Please check it and try again.",
    };
  }

  const local = email.slice(0, atIndex);
  const domain = email.slice(atIndex + 1);

  if (local.length > 64) {
    return {
      valid: false,
      email,
      message: "That email address doesn't look valid. Please check it and try again.",
    };
  }

  if (local.startsWith(".") || local.endsWith(".") || local.includes("..")) {
    return {
      valid: false,
      email,
      message: "That email address doesn't look valid. Please check it and try again.",
    };
  }

  if (domain.startsWith("-") || domain.endsWith("-") || domain.includes("..")) {
    return {
      valid: false,
      email,
      message: "That email address doesn't look valid. Please check it and try again.",
    };
  }

  if (!LOCAL_PART_REGEX.test(local) || !DOMAIN_PART_REGEX.test(domain)) {
    return {
      valid: false,
      email,
      message: "That email address doesn't look valid. Please check it and try again.",
    };
  }

  const domainParts = domain.split(".");
  const tld = domainParts.slice(-1)[0];
  const publicSuffix = domainParts.slice(-2).join(".");

  if (!tld || tld.length < 2 || !/^[a-zA-Z]{2,}$/.test(tld)) {
    return {
      valid: false,
      email,
      message: "That email address doesn't look valid. Please check it and try again.",
    };
  }

  if (!COMMON_TLDS.has(tld) && !COMMON_TLDS.has(publicSuffix)) {
    return {
      valid: false,
      email,
      message: "That email address doesn't look valid. Please check it and try again.",
    };
  }

  const domainName = domainParts.slice(0, -1).join(".");
  if (!domainName || domainName.length < 3) {
    return {
      valid: false,
      email,
      message: "That email address doesn't look valid. Please check it and try again.",
    };
  }

  if (/^\d+$/.test(domainName.replace(/\./g, ""))) {
    return {
      valid: false,
      email,
      message: "That email address doesn't look valid. Please check it and try again.",
    };
  }

  if (isBlockedEmailDomain(domain)) {
    return {
      valid: false,
      email,
      message:
        "Please use a personal or work email address. Temporary email services aren't supported.",
    };
  }

  return { valid: true, email, message: "" };
}

function extractMxHosts(answer) {
  if (!Array.isArray(answer)) {
    return [];
  }

  return answer
    .filter((record) => record.type === 15)
    .map((record) => {
      const host = String(record.data || "")
        .trim()
        .split(/\s+/)
        .pop();
      return String(host || "")
        .replace(/\.$/, "")
        .toLowerCase();
    })
    .filter(Boolean);
}

function getOrganizationalDomain(host) {
  const parts = String(host || "")
    .toLowerCase()
    .split(".")
    .filter(Boolean);

  if (parts.length < 2) {
    return String(host || "").toLowerCase();
  }

  return parts.slice(-2).join(".");
}

const BLOCKED_MX_DOMAINS = new Set([
  "5ax.org",
  "5ax.net",
  "5ax.com",
  "mailinator.com",
  "guerrillamail.com",
  "guerrillamail.net",
  "guerrillamail.org",
  "guerrillamail.biz",
  "guerrillamail.de",
  "sharklasers.com",
  "grr.la",
  "pokemail.net",
  "spam4.me",
  "yopmail.com",
  "trashmail.com",
  "trashmail.me",
  "trashmail.net",
  "maildrop.cc",
  "getnada.com",
  "tempmail.com",
  "temp-mail.org",
  "dispostable.com",
  "mailnesia.com",
  "fakeinbox.com",
  "mintemail.com",
  "mytemp.email",
  "throwawaymail.com",
  "mailcatch.com",
  "mailnull.com",
  "spamgourmet.com",
  "mail-temp.com",
  "emailondeck.com",
  "anonymmail.net",
  "axiz.org",
  "ax05.cn",
]);

const BLOCKED_EMAIL_DOMAINS = new Set([
  "mailinator.com",
  "guerrillamail.com",
  "yopmail.com",
  "tempmail.com",
  "temp-mail.org",
  "10minutemail.com",
  "discard.email",
  "getnada.com",
  "maildrop.cc",
  "sharklasers.com",
  "trashmail.com",
  "example.com",
  "example.net",
  "example.org",
  "test.com",
  "localhost",
]);

function isBlockedMailHost(host) {
  const normalizedHost = String(host || "").toLowerCase();
  if (!normalizedHost) {
    return false;
  }

  if (BLOCKED_MX_DOMAINS.has(normalizedHost)) {
    return true;
  }

  return BLOCKED_MX_DOMAINS.has(getOrganizationalDomain(normalizedHost));
}

function isBlockedEmailDomain(domain) {
  const normalizedDomain = String(domain || "").toLowerCase();
  if (!normalizedDomain) {
    return false;
  }

  if (BLOCKED_EMAIL_DOMAINS.has(normalizedDomain)) {
    return true;
  }

  return BLOCKED_EMAIL_DOMAINS.has(getOrganizationalDomain(normalizedDomain));
}

function hasBlockedMxRecords(answer) {
  return extractMxHosts(answer).some(isBlockedMailHost);
}

export async function verifyEmailDomainExists(domain) {
  if (!domain) {
    return {
      exists: false,
      message: "That email address doesn't look valid. Please check it and try again.",
    };
  }

  const normalizedDomain = String(domain).toLowerCase();
  if (isBlockedEmailDomain(normalizedDomain)) {
    return {
      exists: false,
      message:
        "Please use a personal or work email address. Temporary email services aren't supported.",
    };
  }

  try {
    const mxResponse = await fetch(
      `https://dns.google/resolve?name=${encodeURIComponent(normalizedDomain)}&type=MX`
    );
    const mxData = await mxResponse.json();

    if (mxData.Status === 3) {
      return {
        exists: false,
        message:
          "We couldn't verify that email domain. Please use an email address you can actually receive mail at.",
      };
    }

    if (
      mxData.Status === 0 &&
      Array.isArray(mxData.Answer) &&
      mxData.Answer.length
    ) {
      if (hasBlockedMxRecords(mxData.Answer)) {
        return {
          exists: false,
          message:
            "Please use a personal or work email address. Temporary email services aren't supported.",
        };
      }

      return { exists: true, message: "" };
    }

    const aResponse = await fetch(
      `https://dns.google/resolve?name=${encodeURIComponent(normalizedDomain)}&type=A`
    );
    const aData = await aResponse.json();

    if (aData.Status === 3) {
      return {
        exists: false,
        message:
          "We couldn't verify that email domain. Please use an email address you can actually receive mail at.",
      };
    }

    if (aData.Status === 0 && Array.isArray(aData.Answer) && aData.Answer.length) {
      return { exists: true, message: "" };
    }

    return {
      exists: false,
      message:
        "We couldn't verify that email domain. Please use an email address you can actually receive mail at.",
    };
  } catch (error) {
    return { exists: null, message: "" };
  }
}

export function isValidEmail(value, options) {
  return validateEmail(value, options).valid;
}
